import { ComparisonSpecType, DataTable, DateGranularity, DateWindow, NullableStatus } from '@tableau/metrics-engine';
import * as React from 'react';
import { ComparisonComponent } from './components/comparison/ComparisonComponent';
import { DateWindowComponent } from './components/window/DateWindowComponent';

export interface ConfigurationComponentProps {
  // Feature Flags
  isDateWindowEnabled?: boolean;
  isReferenceLineEnabled?: boolean;
  isHistoricalComparisonsEnabled?: boolean;
  isColorIndicatorsEnabled?: boolean;

  // Context
  canApplyDateWindow?: boolean;
  dateGranularity: DateGranularity;
  formatString?: string;
  data: DataTable;
  disabled?: boolean;
  focusFirstInput?: boolean;

  /**
   * Changing the value of the validation counter will trigger the validation UI.
   * Any invalid fields will wiggle to catch the user's attention, and the
   * first one will become the focused element. The actual numeric value is ignored.
   */
  validationCounter: number;

  // Configuration
  readonly selectedDateWindow: DateWindow | null;
  readonly selectedComparison: ComparisonSpecType | null;
  readonly status: NullableStatus;

  readonly onDateWindowChange: (value: DateWindow | null) => void;
  readonly onComparisonChange: (comparison: ComparisonSpecType | null) => void;
  readonly onStatusChange: (status: NullableStatus) => void;
}

export const ConfigurationComponent = (props: ConfigurationComponentProps): JSX.Element => {
  let firstDropdownRef = props.focusFirstInput ? React.createRef<HTMLButtonElement>() : undefined;

  React.useEffect(() => {
    firstDropdownRef?.current?.focus();
  }, []);

  const shouldRenderDateWindowComponent = props.isDateWindowEnabled && props.canApplyDateWindow;

  return (
    <div>
      {shouldRenderDateWindowComponent && (
        <DateWindowComponent
          dateGranularity={props.dateGranularity}
          data={props.data}
          selectedDateWindow={props.selectedDateWindow}
          onChange={props.onDateWindowChange}
          validationCounter={props.validationCounter}
          disabled={props.disabled}
          dropdownRef={firstDropdownRef}
        />
      )}
      <ComparisonComponent
        dateGranularity={props.dateGranularity}
        canAuthorHistoricalComparison={props.canApplyDateWindow}
        selectedComparison={props.selectedComparison}
        onComparisonChange={props.onComparisonChange}
        isReferenceLineEnabled={props.isReferenceLineEnabled}
        isHistoricalComparisonsEnabled={props.isHistoricalComparisonsEnabled}
        formatString={props.formatString}
        validationCounter={props.validationCounter}
        onStatusChange={props.onStatusChange}
        status={props.status}
        disabled={props.disabled}
        dropdownRef={!shouldRenderDateWindowComponent ? firstDropdownRef : undefined}
      />
    </div>
  );
};
